import { PROVIDER_TYPE } from "@packages/core";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuPlus } from "react-icons/lu";
import * as v from "valibot";

import {
  PageName,
  PageNumberSelector,
  PageSizeSelector,
  SkeletonScreen,
  TABLE_STYLE,
  buttonStyles,
  copyToClipboard,
} from "#components";
import { ENVIRONMENT_VARIABLE } from "#environment-variable";
import { useProviderInfiniteQuery } from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/providers/")({
  component: Component,
  validateSearch: v.object({
    pageSize: v.fallback(
      v.optional(
        v.pipe(v.union([v.number(), v.pipe(v.string(), v.toNumber())]), v.integer(), v.minValue(1)),
      ),
      undefined,
    ),
  }),
});

function Component() {
  const { pageSize = 100 } = Route.useSearch();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(0);

  const providerInfiniteQuery = useProviderInfiniteQuery({ limit: pageSize });

  const { providers, numberOfProviders } = useMemo(
    function () {
      return {
        providers: providerInfiniteQuery.data?.pages.at(pageNumber)?.providers ?? [],
        numberOfProviders:
          providerInfiniteQuery.data?.pages.reduce(function (numberOfProviders, page) {
            return numberOfProviders + page.providers.length;
          }, 0) ?? 0,
      };
    },
    [providerInfiniteQuery.data, pageNumber],
  );

  return (
    <main className={css({ display: "flex", flexDirection: "column" })}>
      <PageName>{t("%ProviderManagement.0%")}</PageName>
      <p className={css({ fontSize: "24px", fontWeight: 700 })}>{t("%ProviderManagement.0%")}</p>

      <div
        className={css({
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
        })}
      >
        <div></div>
        <div>
          <Link
            className={css({ alignItems: "center", display: "flex", gap: "8px" })}
            to="/management/providers/registration"
          >
            <LuPlus />
            <p>{t("%AddProvider.0%")}</p>
          </Link>
        </div>
      </div>

      <div
        className={css(TABLE_STYLE, {
          flexGrow: 1,
          gridTemplateColumns: "128px 140px minmax(148px, 1fr) 280px",
          marginTop: "6px",
          marginBottom: "auto",
          maxHeight: "max-content",
        })}
      >
        <div data-part="head" data-scope="table">
          <div data-part="head-row" data-scope="table">
            <div data-part="head-cell" data-scope="table">
              {t("%Type.0%")}
            </div>
            <div data-part="head-cell" data-scope="table">
              {t("%ProviderKey.0%")}
            </div>
            <div data-part="head-cell" data-scope="table">
              {t("%ProviderName.0%")}
            </div>
            <div data-part="head-cell" data-scope="table" />
          </div>
        </div>

        <div data-part="body" data-scope="table">
          {providers.map(function (provider) {
            return (
              <div data-part="body-row" data-scope="table" key={provider.providerKey}>
                <div data-part="body-cell" data-scope="table">
                  {t("%ProviderType." + provider.type + ".0%")}
                </div>
                <div data-hide-below="sm" data-part="body-cell" data-scope="table">
                  {provider.providerKey}
                </div>
                <div data-part="body-cell" data-scope="table">
                  {provider.name}
                </div>
                <div data-hide-below="sm" data-part="body-cell" data-scope="table">
                  {provider.type === PROVIDER_TYPE.AAMS_SERVER && (
                    <button
                      className={buttonStyles({ color: "primary" })}
                      onClick={function () {
                        switch (provider.type) {
                          case PROVIDER_TYPE.AAMS_SERVER: {
                            const baseUrl =
                              ENVIRONMENT_VARIABLE.FUNCTION_ENTRY_POINT + "/partnerships/biosilver";
                            copyToClipboard(
                              [
                                "- プロバイダー名: " + provider.name,
                                "- プロバイダーキー: " + provider.providerKey,
                                "- X-Reha30-Api-Keyヘッダー: " + provider.apiKey,
                                "  ※代わりにX-Api-Keyヘッダーに設定も可能です。",
                                "- X-Reha30-Api-Secretヘッダー: " + provider.apiSecret,
                                "  ※代わりにX-Api-Secretヘッダーに設定も可能です。",
                                "- ベースURL: " + baseUrl,
                                "  - バイタルデータ通知: POST " +
                                  baseUrl +
                                  "/api/biosilver/v1/carerecords",
                                "  - アラートデータ通知: POST " +
                                  baseUrl +
                                  "/api/biosilver/v1/alert",
                                "  - 利用者取得: GET " + baseUrl + "/api/biosilver/v1/patients",
                                "  - 評価データ取得: GET " +
                                  baseUrl +
                                  "/api/biosilver/v1/reha30/assessments",
                              ].join("\n"),
                            );
                            break;
                          }
                        }
                      }}
                    >
                      {t("%2%")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {providerInfiniteQuery.isFetching &&
            Array.from({ length: Math.max(pageSize - providers.length, 0) }, function (_, index) {
              const string1 = "　　　　";
              const string2 = string1.repeat(2);
              return (
                <div data-part="body-row" data-scope="table" key={index}>
                  <div data-part="body-cell" data-scope="table">
                    <SkeletonScreen>{string1}</SkeletonScreen>
                  </div>
                  <div data-part="body-cell" data-scope="table">
                    <SkeletonScreen>{string1}</SkeletonScreen>
                  </div>
                  <div data-part="body-cell" data-scope="table">
                    <SkeletonScreen>{string2}</SkeletonScreen>
                  </div>
                  <div data-part="body-cell" data-scope="table" />
                </div>
              );
            })}
        </div>
      </div>

      <div className={css({ display: "flex", justifyContent: "space-between", marginTop: "8px" })}>
        <PageNumberSelector
          hasNextPage={providerInfiniteQuery.hasNextPage}
          isFetching={providerInfiniteQuery.isFetching}
          numberOfItems={numberOfProviders}
          onNextPage={function () {
            if (providerInfiniteQuery.hasNextPage && !providerInfiniteQuery.isFetching) {
              setPageNumber(function (pageNumber) {
                return pageNumber + 1;
              });
              providerInfiniteQuery.fetchNextPage().then(function (providerInfiniteQuery) {
                setPageNumber(function (pageNumber) {
                  const numberOfProviders =
                    providerInfiniteQuery.data?.pages.reduce(function (numberOfBeds, page) {
                      return numberOfBeds + page.providers.length;
                    }, 0) ?? 0;
                  return Math.max(
                    Math.min(pageNumber + 1, Math.ceil(numberOfProviders / pageSize) - 1),
                    0,
                  );
                });
              });
            }
          }}
          pageNumber={pageNumber}
          pageSize={pageSize}
          setPageNumber={setPageNumber}
        />
        <PageSizeSelector
          onChange={function (pageSize) {
            setPageNumber(0);
            navigate({
              replace: true,
              search(parameters) {
                return { ...parameters, pageSize };
              },
              to: ".",
            });
          }}
          options={
            new Set(
              [pageSize, 50, 100, 150].sort(function (a, b) {
                return a - b;
              }),
            )
          }
          value={pageSize}
        />
      </div>
    </main>
  );
}
